import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import api from '../services/api';
import PageHeader from '../components/PageHeader';

interface User {
  id: number;
  username: string;
  fullName: string;
  email: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'USER';
  enabled: boolean;
  departments: Department[];
  createdAt: string;
  updatedAt: string;
}

interface Department {
  id: number;
  name: string;
  description: string;
}

interface UserFormData {
  username: string;
  fullName: string;
  email: string;
  password: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'USER';
  departmentIds: number[];
  enabled: boolean;
}

interface FormErrors {
  username: string;
  fullName: string;
  password: string;
  departmentIds: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    fullName: '',
    email: '',
    password: '',
    role: 'USER',
    departmentIds: [],
    enabled: true
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [formErrors, setFormErrors] = useState<FormErrors>({
    username: '',
    fullName: '',
    password: '',
    departmentIds: ''
  });
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    loadUsers();
    loadDepartments();
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    try {
      const response = await api.get('/api/users/current');
      setCurrentUser(response.data);
    } catch (error) {
      console.error('获取当前用户信息失败:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('加载用户失败:', error);
      showSnackbar('加载用户失败', 'error');
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await api.get('/api/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('加载部门失败:', error);
    }
  };

  // 检查是否有权限操作超级管理员
  const hasPermissionToModifySuperAdmin = (targetUser: User): boolean => {
    // 如果当前用户不是超级管理员，则无权限操作任何超级管理员
    if (currentUser?.role !== 'SUPER_ADMIN') {
      return false;
    }
    
    // 如果是超级管理员，可以操作其他超级管理员，但不能操作自己
    if (targetUser.role === 'SUPER_ADMIN') {
      return targetUser.id !== currentUser.id;
    }
    
    return true;
  };

  // 检查是否有权限删除用户
  const hasPermissionToDeleteUser = (targetUser: User): boolean => {
    // 如果当前用户不是超级管理员，则无权限删除任何用户
    if (currentUser?.role !== 'SUPER_ADMIN') {
      return false;
    }
    
    // 超级管理员不能删除自己
    if (targetUser.id === currentUser?.id) {
      return false;
    }
    
    return true;
  };

  const handleOpenDialog = (user?: User) => {
    // 检查权限
    if (user && !hasPermissionToModifySuperAdmin(user)) {
      if (user.role === 'SUPER_ADMIN') {
        if (user.id === currentUser?.id) {
          showSnackbar('不能修改自己的超级管理员账号信息', 'error');
        } else {
          showSnackbar('无权限修改超级管理员用户信息', 'error');
        }
      } else {
        showSnackbar('无权限修改用户信息', 'error');
      }
      return;
    }

    // 清除之前的错误
    setFormErrors({
      username: '',
      fullName: '',
      password: '',
      departmentIds: ''
    });
    
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        fullName: user.fullName,
        email: user.email,
        password: '',
        role: user.role,
        departmentIds: user.role === 'ADMIN' || user.role === 'SUPER_ADMIN' ? departments.map(d => d.id) : user.departments.map(d => d.id),
        enabled: user.enabled
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        fullName: '',
        email: '',
        password: '',
        role: 'USER',
        departmentIds: [],
        enabled: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {
      username: '',
      fullName: '',
      password: '',
      departmentIds: ''
    };
    
    let isValid = true;
    
    // 验证用户名
    if (!formData.username.trim()) {
      errors.username = '用户名不能为空';
      isValid = false;
    }
    
    // 验证姓名
    if (!formData.fullName.trim()) {
      errors.fullName = '姓名不能为空';
      isValid = false;
    }
    
    // 验证密码（新增用户时必填，编辑用户时可选）
    if (!editingUser && !formData.password.trim()) {
      errors.password = '密码不能为空';
      isValid = false;
    }
    
    // 验证所属部门（普通用户必填，管理员和超级管理员自动拥有所有部门权限）
    if (formData.role === 'USER' && formData.departmentIds.length === 0) {
      errors.departmentIds = '请选择至少一个部门';
      isValid = false;
    }
    
    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      if (editingUser) {
        await api.put(`/api/users/${editingUser.id}`, formData);
        showSnackbar('用户更新成功');
      } else {
        await api.post('/api/users', formData);
        showSnackbar('用户创建成功');
      }
      handleCloseDialog();
      loadUsers();
    } catch (error) {
      console.error('保存用户失败:', error);
      showSnackbar('保存用户失败', 'error');
    }
  };

  const handleDeleteUser = async (userId: number) => {
    const targetUser = users.find(user => user.id === userId);
    if (!targetUser) {
      showSnackbar('用户不存在', 'error');
      return;
    }

    // 检查权限
    if (!hasPermissionToDeleteUser(targetUser)) {
      if (targetUser.id === currentUser?.id) {
        showSnackbar('不能删除自己的账号', 'error');
      } else if (targetUser.role === 'SUPER_ADMIN') {
        showSnackbar('无权限删除超级管理员用户', 'error');
      } else {
        showSnackbar('无权限删除用户', 'error');
      }
      return;
    }

    if (window.confirm('确定要删除这个用户吗？')) {
      try {
        await api.delete(`/api/users/${userId}`);
        showSnackbar('用户删除成功');
        loadUsers();
      } catch (error) {
        console.error('删除用户失败:', error);
        showSnackbar('删除用户失败', 'error');
      }
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getRoleLabel = (role: string) => {
    const roleMap = {
      'SUPER_ADMIN': '超级管理员',
      'ADMIN': '管理员',
      'USER': '普通用户'
    };
    return roleMap[role as keyof typeof roleMap] || role;
  };

  const getRoleColor = (role: string) => {
    const colorMap = {
      'SUPER_ADMIN': 'error',
      'ADMIN': 'warning',
      'USER': 'default'
    };
    return colorMap[role as keyof typeof colorMap] || 'default';
  };

  return (
    <Box>
      <PageHeader
        title="用户管理"
        subtitle="创建账号、分配角色与部门权限"
        breadcrumbs={[{ label: '系统管理' }, { label: '用户管理' }]}
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            添加用户
          </Button>
        }
      />

      <Paper elevation={0} sx={{ width: '100%', overflow: 'hidden', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>用户名</TableCell>
                <TableCell>姓名</TableCell>
                <TableCell>邮箱</TableCell>
                <TableCell>角色</TableCell>
                <TableCell>部门</TableCell>
                <TableCell>状态</TableCell>
                <TableCell>创建时间</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                const canEdit = hasPermissionToModifySuperAdmin(user);
                const canDelete = hasPermissionToDeleteUser(user);
                
                return (
                  <TableRow key={user.id}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.fullName}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={getRoleLabel(user.role)}
                        color={getRoleColor(user.role) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {user.departments.map((dept) => (
                          <Chip
                            key={dept.id}
                            label={dept.name}
                            size="small"
                            variant="outlined"
                            icon={<BusinessIcon />}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.enabled ? '启用' : '禁用'}
                        color={user.enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(user)}
                        color="primary"
                        disabled={!canEdit}
                        title={!canEdit ? '无权限修改此用户' : '编辑用户'}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteUser(user.id)}
                        color="error"
                        disabled={!canDelete}
                        title={!canDelete ? '无权限删除此用户' : '删除用户'}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 用户表单对话框 */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUser ? '编辑用户' : '添加用户'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="用户名 *"
                value={formData.username}
                onChange={(e) => {
                  setFormData({ ...formData, username: e.target.value });
                  if (formErrors.username) {
                    setFormErrors({ ...formErrors, username: '' });
                  }
                }}
                disabled={!!editingUser}
                error={!!formErrors.username}
                helperText={formErrors.username}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="姓名 *"
                value={formData.fullName}
                onChange={(e) => {
                  setFormData({ ...formData, fullName: e.target.value });
                  if (formErrors.fullName) {
                    setFormErrors({ ...formErrors, fullName: '' });
                  }
                }}
                error={!!formErrors.fullName}
                helperText={formErrors.fullName}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="邮箱"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={editingUser ? '密码' : '密码 *'}
                type="password"
                value={formData.password}
                onChange={(e) => {
                  setFormData({ ...formData, password: e.target.value });
                  if (formErrors.password) {
                    setFormErrors({ ...formErrors, password: '' });
                  }
                }}
                error={!!formErrors.password}
                helperText={formErrors.password || (editingUser ? '留空则不修改密码' : '')}
                required={!editingUser}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>角色</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => {
                    const newRole = e.target.value as any;
                    const newFormData = { ...formData, role: newRole };
                    
                    // 如果选择管理员或超级管理员，自动设置所有部门
                    if (newRole === 'ADMIN' || newRole === 'SUPER_ADMIN') {
                      newFormData.departmentIds = departments.map(d => d.id);
                    }
                    
                    setFormData(newFormData);
                    
                    // 清除部门错误（因为管理员和超级管理员自动拥有所有部门权限）
                    if (newRole === 'ADMIN' || newRole === 'SUPER_ADMIN') {
                      setFormErrors({ ...formErrors, departmentIds: '' });
                    }
                  }}
                  label="角色"
                >
                  <MenuItem value="USER">普通用户</MenuItem>
                  <MenuItem value="ADMIN">管理员</MenuItem>
                  <MenuItem value="SUPER_ADMIN">超级管理员</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  />
                }
                label="启用账户"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!formErrors.departmentIds}>
                <InputLabel>所属部门 {formData.role === 'USER' ? '*' : ''}</InputLabel>
                <Select
                  multiple
                  value={formData.role === 'ADMIN' || formData.role === 'SUPER_ADMIN' ? departments.map(d => d.id) : formData.departmentIds}
                  onChange={(e) => {
                    setFormData({ ...formData, departmentIds: e.target.value as number[] });
                    if (formErrors.departmentIds) {
                      setFormErrors({ ...formErrors, departmentIds: '' });
                    }
                  }}
                  label={`所属部门 ${formData.role === 'USER' ? '*' : ''}`}
                  disabled={formData.role === 'ADMIN' || formData.role === 'SUPER_ADMIN'}
                  required={formData.role === 'USER'}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const dept = departments.find(d => d.id === value);
                        return (
                          <Chip key={value} label={dept?.name || value} size="small" />
                        );
                      })}
                    </Box>
                  )}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.departmentIds && (
                  <FormHelperText error>{formErrors.departmentIds}</FormHelperText>
                )}
                {(formData.role === 'ADMIN' || formData.role === 'SUPER_ADMIN') && !formErrors.departmentIds && (
                  <FormHelperText>
                    管理员和超级管理员可以访问所有部门，无需设置特定部门
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingUser ? '更新' : '创建'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
